// RescheduleBuildModal — Robert's "Reschedule build" button (Oct 16, 2026).
//
// Greg: "sometimes, rarely, the crew will pull up to the house and decide to
// cancel because some guys didn't show up; also sometimes we have bad weather
// and need to reschedule morning of ... I would rather EDIT the event already
// on Google Calendar ... Sometimes we are rescheduling a few days out as well."
//
// One confirm does all of it on the server (build-calendar edge function):
// the deal's date, the Google event (moved, DATE CHANGE in the title, guests
// notified), the SRS order re-send with the new drop date, the homeowner
// text, the RM's in-portal notice, and the audit rows. The modal then shows
// each step's result, so a Google or email failure never looks like success.
//
// "No new date yet" calls the build off: the date comes off the deal, the
// event is retitled CALLED OFF, and the homeowner hears a new date is coming.
// Picking a date later runs the normal reschedule and closes the call-off.
//
// The reason is a fixed KEY (the homeowner texts key their wording on it);
// Robert's note is internal and never leaves the CRM.
//
// Oct 26, 2026: a build can run more than one day. The new dates are a first
// day and a last day (1 / 2 / 3 day buttons); the length carries over when
// the first day moves, and keeping the first day while changing only the last
// is a valid change (a one-day build becomes two). The event, the SRS order
// and the homeowner text follow (build-calendar v9, dispatch-webhook v8).

import { useEffect, useMemo, useState, type ReactNode } from "react";
import Modal from "./Modal";
import type { Deal } from "../../../lib/types";
import {
  buildCalendar, REASON_OPTIONS, nextWeekdays, srsDropDateISO, isWeekendISO, fmtShortDate, fmtBuildSpan, todayISO,
  addWeekdaysISO, buildDayCount, buildEndProblem, normalizeBuildEnd,
  type ReasonKey, type RescheduleResult, type BuildReschedule, type CandidateEvent,
} from "../../../lib/buildCalendar";
import { fetchLatestOrderSend, type MaterialOrderSend } from "../../../lib/srsMaterialOrder";

type DealLite = Pick<Deal, "id" | "homeowner_name" | "property_street" | "property_city" | "build_scheduled_date" | "build_end_date"> & {
  pm_workbook?: unknown;
};

interface Props {
  open: boolean;
  deal: DealLite;
  /** The open call-off on this deal, when the build was called off earlier with no date. */
  openCallOff?: BuildReschedule | null;
  onClose: () => void;
  /** Fires once the server has done its work (before the modal closes). */
  onDone?: (result: RescheduleResult) => void;
}

type Mode = "date" | "calloff";
type Phase = "form" | "running" | "done";

export default function RescheduleBuildModal({ open, deal, openCallOff = null, onClose, onDone }: Props) {
  const currentDate = deal.build_scheduled_date ? deal.build_scheduled_date.slice(0, 10) : null;
  const currentEnd = currentDate ? normalizeBuildEnd(currentDate, deal.build_end_date) : null;
  const currentDays = currentDate ? buildDayCount(currentDate, currentEnd) : 1;
  const wb = (deal.pm_workbook ?? null) as { drop_notes?: string } | null;

  const [mode, setMode] = useState<Mode>("date");
  const [newDate, setNewDate] = useState("");
  // The new last day; "" = a one-day build.
  const [newEnd, setNewEnd] = useState("");
  const [dropDate, setDropDate] = useState("");
  const [dropTouched, setDropTouched] = useState(false);
  const [dropNotes, setDropNotes] = useState(wb?.drop_notes?.trim() || "Early AM");
  const [reason, setReason] = useState<ReasonKey | "">("");
  const [note, setNote] = useState("");
  const [cancelledOnSite, setCancelledOnSite] = useState(false);
  const [resendSrs, setResendSrs] = useState(false);
  const [srsTouched, setSrsTouched] = useState(false);
  const [textHomeowner, setTextHomeowner] = useState(true);
  const [priorOrder, setPriorOrder] = useState<MaterialOrderSend | null | undefined>(undefined);

  const [phase, setPhase] = useState<Phase>("form");
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<RescheduleResult | null>(null);
  const [adopting, setAdopting] = useState<string | null>(null);

  // Drop date trails the new build date until Robert edits it himself: the
  // last weekday before the build (SRS Marietta delivers Mon–Fri only, so a
  // Monday build drops the Friday before — Greg, Oct 16, 2026).
  useEffect(() => {
    if (dropTouched) return;
    setDropDate(newDate ? srsDropDateISO(newDate) : "");
  }, [newDate, dropTouched]);

  // The length carries over when the first day moves (a 2-day build stays 2 days).
  const dayCount = newDate ? buildDayCount(newDate, newEnd || null) : currentDays;
  function pickStart(d: string) {
    setNewDate(d);
    setNewEnd(d && dayCount > 1 ? addWeekdaysISO(d, dayCount - 1) : "");
  }
  function pickDays(n: number) {
    if (!newDate) return;
    setNewEnd(n > 1 ? addWeekdaysISO(newDate, n - 1) : "");
  }
  const newEndISO = newDate ? normalizeBuildEnd(newDate, newEnd || null) : null;
  const endProblem = newDate && newEnd ? buildEndProblem(newDate, newEnd) : null;
  const startMoved = !!newDate && newDate !== currentDate;

  useEffect(() => {
    if (!open) return;
    let alive = true;
    fetchLatestOrderSend(deal.id).then((o) => { if (alive) setPriorOrder(o); });
    return () => { alive = false; };
  }, [open, deal.id]);
  // Re-send to SRS by default only when an order already went out and the
  // first day (so the drop) moves; a longer or shorter build needs no new drop.
  const priorSent = !!priorOrder && priorOrder.send_status === "sent";
  useEffect(() => {
    if (!srsTouched) setResendSrs(priorSent && (startMoved || !newDate));
  }, [priorSent, startMoved, newDate, srsTouched]);

  const chips = useMemo(() => nextWeekdays(todayISO(), 5), []);
  const isToday = !!currentDate && currentDate === todayISO();
  const weekend = !!newDate && isWeekendISO(newDate);
  const sameDate = !!newDate && newDate === currentDate && newEndISO === currentEnd;

  const canSubmit =
    phase === "form" && !!reason &&
    (mode === "calloff" ? !!currentDate : (!!newDate && !weekend && !sameDate && !endProblem));

  async function submit() {
    setErr(null);
    if (!reason) return setErr("Pick a reason.");
    if (mode === "date") {
      if (!newDate) return setErr("Pick the new build date.");
      if (weekend) return setErr("Builds aren't scheduled on weekends — pick a weekday.");
      if (sameDate) return setErr("Those are already the build dates.");
      if (endProblem) return setErr(endProblem);
    }
    setPhase("running");
    try {
      const r = await buildCalendar<RescheduleResult>({
        action: "reschedule",
        deal_id: deal.id,
        new_build_date: mode === "date" ? newDate : null,
        new_build_end_date: mode === "date" ? newEndISO : null,
        new_drop_date: mode === "date" ? (dropDate || undefined) : undefined,
        drop_notes: dropNotes,
        reason_key: reason,
        note: note.trim() || undefined,
        cancelled_on_site: cancelledOnSite,
        resend_srs: mode === "date" && resendSrs,
        text_homeowner: textHomeowner,
      });
      setResult(r);
      setPhase("done");
      onDone?.(r);
    } catch (e) {
      setErr((e as Error).message);
      setPhase("form");
    }
  }

  async function adopt(c: CandidateEvent) {
    if (!result) return;
    setAdopting(c.id);
    setErr(null);
    try {
      const r = await buildCalendar<{ ok: boolean; status: "updated" | "adopted"; event_id: string; html_link: string | null }>({
        action: "adopt_event", deal_id: deal.id, event_id: c.id, reschedule_id: result.reschedule_id,
      });
      setResult({ ...result, calendar: { status: r.status, event_id: r.event_id, html_link: r.html_link } });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setAdopting(null);
    }
  }

  const title = openCallOff && !currentDate ? "Set the new build date" : "Reschedule build";
  const subtitle = `${deal.homeowner_name ?? "(no name)"} · ${deal.property_street ?? ""}${currentDate ? ` · currently ${fmtBuildSpan(currentDate, currentEnd)}${isToday ? " (today)" : ""}` : openCallOff ? " · called off, no date yet" : ""}`;
  const chipDays = currentDate && currentDate >= todayISO() && !chips.includes(currentDate) ? [currentDate, ...chips] : chips;

  const close = () => { if (phase !== "running") onClose(); };

  return (
    <Modal
      open={open}
      title={title}
      subtitle={subtitle}
      onClose={close}
      footer={
        phase === "done" ? (
          <button onClick={onClose} className="px-4 py-1.5 rounded bg-slate-800 text-white text-sm font-medium hover:bg-slate-900">Done</button>
        ) : (
          <>
            <button onClick={close} disabled={phase === "running"} className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50">Cancel</button>
            <button
              onClick={submit}
              disabled={!canSubmit}
              className={"px-4 py-1.5 rounded text-white text-sm font-medium disabled:opacity-50 " + (mode === "calloff" ? "bg-rose-600 hover:bg-rose-700" : "bg-bardin-orange hover:opacity-90")}
            >
              {phase === "running" ? "Working…" : mode === "calloff" ? "Call off today's build" : "Reschedule"}
            </button>
          </>
        )
      }
    >
      {phase !== "done" && (
        <>
          {openCallOff && !currentDate && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800">
              Called off {new Date(openCallOff.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {REASON_OPTIONS.find((r) => r.key === openCallOff.reason_key)?.label ?? openCallOff.reason_key}
              {openCallOff.old_build_date ? ` · was ${fmtShortDate(openCallOff.old_build_date)}` : ""}. Picking a date below closes it and sends the new date to everyone.
            </div>
          )}

          {/* Date or call-off */}
          {currentDate && (
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setMode("date")}
                className={"flex-1 rounded-lg border px-3 py-1.5 text-xs font-semibold " + (mode === "date" ? "border-bardin-orange bg-orange-50 text-orange-800" : "border-slate-200 text-slate-600 hover:bg-slate-50")}
              >
                New date
              </button>
              <button
                type="button"
                onClick={() => setMode("calloff")}
                className={"flex-1 rounded-lg border px-3 py-1.5 text-xs font-semibold " + (mode === "calloff" ? "border-rose-500 bg-rose-50 text-rose-800" : "border-slate-200 text-slate-600 hover:bg-slate-50")}
              >
                No new date yet
              </button>
            </div>
          )}

          {mode === "date" ? (
            <div className="space-y-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">New build dates</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {chipDays.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => pickStart(d)}
                      className={"px-2.5 py-1 rounded-full border text-xs font-medium " + (newDate === d ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")}
                    >
                      {fmtShortDate(d)}{d === currentDate ? <span className={newDate === d ? "text-slate-300" : "text-slate-400"}> · same first day</span> : null}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-0.5">First day</label>
                    <input
                      type="date"
                      value={newDate}
                      onChange={(e) => pickStart(e.target.value)}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                      aria-label="New first build day"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-0.5">Last day</label>
                    <input
                      type="date"
                      value={newEnd || newDate}
                      min={newDate || undefined}
                      disabled={!newDate}
                      onChange={(e) => setNewEnd(e.target.value && e.target.value !== newDate ? e.target.value : "")}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm disabled:bg-slate-50"
                      aria-label="New last build day"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="text-[11px] text-slate-500 mr-0.5">How long:</span>
                  {[1, 2, 3].map((n) => (
                    <button
                      key={n}
                      type="button"
                      disabled={!newDate}
                      onClick={() => pickDays(n)}
                      className={"px-2.5 py-1 rounded-full border text-xs font-medium disabled:opacity-50 " + (dayCount === n ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")}
                    >
                      {n === 1 ? "1 day" : `${n} days`}
                    </button>
                  ))}
                </div>
                {weekend && <p className="text-xs text-red-600 mt-1">No weekend builds — pick a weekday.</p>}
                {sameDate && <p className="text-xs text-red-600 mt-1">Those are already the build dates.</p>}
                {endProblem && <p className="text-xs text-red-600 mt-1">{endProblem}</p>}
                {newDate && !weekend && !sameDate && !endProblem && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    {dayCount === 1 ? "One day" : `${dayCount} days`}: {fmtBuildSpan(newDate, newEndISO)}{!startMoved ? " (same first day, so the drop stays put)" : ""}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Material drop</label>
                  <input
                    type="date"
                    value={dropDate}
                    onChange={(e) => { setDropTouched(true); setDropDate(e.target.value); }}
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">The weekday before the first build day (Friday for a Monday build) unless you change it.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Drop notes</label>
                  <input
                    type="text"
                    value={dropNotes}
                    onChange={(e) => setDropNotes(e.target.value)}
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                    placeholder="Early AM"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800">
              The build on {fmtBuildSpan(currentDate, currentEnd)} comes off the schedule with no date. The calendar event is kept and retitled CALLED OFF, the homeowner hears a new date is coming, and SRS is not emailed until there is one. Open this again to set the date.
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {REASON_OPTIONS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setReason(r.key)}
                  title={r.hint}
                  className={"text-left rounded-lg border px-3 py-1.5 text-xs " + (reason === r.key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50")}
                >
                  <span className="font-semibold">{r.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Note <span className="text-slate-400 font-normal">(internal — never sent to the homeowner)</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              placeholder="Who was out, what happened, anything the next person should know."
            />
          </div>

          <div className="space-y-1.5 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={cancelledOnSite} onChange={(e) => setCancelledOnSite(e.target.checked)} className="accent-rose-600" />
              <span>Cancelled on site this morning <span className="text-slate-400 text-xs">(the crew was already at the house)</span></span>
            </label>
            {mode === "date" && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={resendSrs} onChange={(e) => { setSrsTouched(true); setResendSrs(e.target.checked); }} disabled={!priorOrder || priorOrder.send_status !== "sent"} className="accent-emerald-600" />
                <span>
                  Re-send the material order to SRS with the new drop date
                  {priorOrder === undefined ? "" : priorOrder && priorOrder.send_status === "sent"
                    ? <span className="text-slate-400 text-xs"> (last sent {new Date(priorOrder.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })})</span>
                    : <span className="text-slate-400 text-xs"> (no order has been sent for this job)</span>}
                </span>
              </label>
            )}
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={textHomeowner} onChange={(e) => setTextHomeowner(e.target.checked)} className="accent-emerald-600" />
              <span>Text the homeowner <span className="text-slate-400 text-xs">(untick if you already called them)</span></span>
            </label>
          </div>

          <p className="text-xs text-slate-500">
            The Google Calendar event is edited in place with DATE CHANGE in the title and the guests get Google's update. The RM gets an in-portal notice.
          </p>

          {err && <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>}
        </>
      )}

      {phase === "done" && result && (
        <div className="space-y-2 text-sm">
          <StepRow ok label="Deal updated">
            {result.deal.new_build_date
              ? <>Build {result.deal.old_build_date ? `moved from ${fmtBuildSpan(result.deal.old_build_date, result.deal.old_build_end_date)} ` : "set "}to <strong>{fmtBuildSpan(result.deal.new_build_date, result.deal.new_build_end_date)}</strong>{result.deal.new_drop_date ? ` · drop ${fmtShortDate(result.deal.new_drop_date)}` : ""}</>
              : <>Build on {fmtBuildSpan(result.deal.old_build_date, result.deal.old_build_end_date)} called off · no new date yet</>}
          </StepRow>

          <CalendarStep result={result} adopting={adopting} onAdopt={adopt} />

          {result.deal.new_build_date && (
            <StepRow ok={result.srs.status === "sent"} warn={result.srs.status === "failed"} label="SRS order">
              {result.srs.status === "sent" && <>Re-sent with DATE CHANGE in the subject{result.srs.subject ? <span className="text-slate-400"> · {result.srs.subject}</span> : null}</>}
              {result.srs.status === "skipped" && "Not re-sent (unticked)"}
              {result.srs.status === "no_prior_order" && "No order had been sent for this job, so nothing to re-send"}
              {result.srs.status === "failed" && <>Didn't go out: {result.srs.error ?? "unknown error"}. Use Calendar / resend order on the PM sheet.</>}
            </StepRow>
          )}

          <StepRow ok={result.homeowner_text.status === "sent"} warn={result.homeowner_text.status === "failed"} label="Homeowner text">
            {result.homeowner_text.status === "sent" && "Sent"}
            {result.homeowner_text.status === "skipped" && "Not sent (unticked)"}
            {result.homeowner_text.status === "not_sent" && <>Not sent — {result.homeowner_text.reason === "no_phone" ? "no phone on file" : result.homeowner_text.reason === "opted_out" ? "they opted out of texts" : result.homeowner_text.reason ?? "no phone"}</>}
            {result.homeowner_text.status === "failed" && <>Couldn't send: {result.homeowner_text.error}</>}
          </StepRow>

          <StepRow ok={result.rm.notified > 0} label="RM notified">
            {result.rm.notified > 0 ? `${result.rm.notified} in-portal notice${result.rm.notified === 1 ? "" : "s"}` : "No RM on the deal to notify"}
          </StepRow>
          {result.trades && result.trades.length > 0 && (
            <StepRow ok={false} warn label="Trades on this job">
              {result.trades.map((t) => (
                <span key={t.id} className="block">
                  {t.work}{t.vendor ? ` (${t.vendor})` : ""}{t.date ? ` on ${fmtShortDate(t.date)}` : ""} no longer fits: {t.reason}
                </span>
              ))}
              <span className="block mt-0.5">Flagged on the Builds board's Trades view. Nothing was moved on Other Trades.</span>
            </StepRow>
          )}

          {err && <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>}
        </div>
      )}
    </Modal>
  );
}

function StepRow({ ok, warn = false, label, children }: { ok: boolean; warn?: boolean; label: string; children: ReactNode }) {
  const tone = ok ? "text-emerald-700" : warn ? "text-rose-700" : "text-amber-700";
  const mark = ok ? "✓" : warn ? "✕" : "–";
  return (
    <div className="flex gap-2 items-start">
      <span className={"w-4 shrink-0 font-bold " + tone}>{mark}</span>
      <div className="min-w-0">
        <div className="font-semibold text-slate-800">{label}</div>
        <div className="text-xs text-slate-600">{children}</div>
      </div>
    </div>
  );
}

function CalendarStep({ result, adopting, onAdopt }: { result: RescheduleResult; adopting: string | null; onAdopt: (c: CandidateEvent) => void }) {
  const c = result.calendar;
  const link = c.html_link ? <a href={c.html_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Open in Google ↗</a> : null;
  if (c.status === "updated" || c.status === "adopted") {
    return (
      <StepRow ok label="Google Calendar">
        {result.deal.new_build_date ? "Event moved, title now starts with DATE CHANGE, guests notified" : "Event retitled CALLED OFF, guests notified"}
        {c.status === "adopted" ? " · the CRM now holds this event's id" : ""} {link}
      </StepRow>
    );
  }
  if (c.status === "ambiguous" && c.candidates?.length) {
    return (
      <StepRow ok={false} label="Google Calendar">
        <div>More than one event looks like this build. Pick the right one and it gets updated:</div>
        <div className="mt-1 space-y-1">
          {c.candidates.map((cand) => (
            <div key={cand.id} className="flex items-center gap-2">
              <button
                type="button"
                disabled={!!adopting}
                onClick={() => onAdopt(cand)}
                className="px-2 py-0.5 rounded bg-slate-900 text-white text-[11px] font-semibold disabled:opacity-50"
              >
                {adopting === cand.id ? "Updating…" : "Use this one"}
              </button>
              <span className="truncate">{cand.summary}{cand.startDate ? ` · ${fmtShortDate(cand.startDate)}` : ""}{cand.location ? ` · ${cand.location}` : ""}</span>
            </div>
          ))}
        </div>
        {c.day_url && <div className="mt-1"><a href={c.day_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">or edit it by hand in Google ↗</a></div>}
      </StepRow>
    );
  }
  if (c.status === "not_found") {
    return (
      <StepRow ok={false} label="Google Calendar">
        Couldn't find the event for this address. {c.day_url && <a href={c.day_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Open that day in Google to edit it by hand ↗</a>}
      </StepRow>
    );
  }
  if (c.status === "not_configured") {
    return (
      <StepRow ok={false} label="Google Calendar">
        Not connected yet, so the event wasn't touched. {c.day_url && <a href={c.day_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Open that day in Google to edit it by hand ↗</a>}
      </StepRow>
    );
  }
  if (c.status === "error") {
    return (
      <StepRow ok={false} warn label="Google Calendar">
        Google refused the change: {c.error ?? "unknown error"}. {c.day_url && <a href={c.day_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Edit it by hand ↗</a>}
      </StepRow>
    );
  }
  return <StepRow ok={false} label="Google Calendar">Skipped</StepRow>;
}
